import { calcularSalario } from './financas';
import { gerarClube } from './gerador';
import { gerarCalendario, type Calendario } from './liga';
import { completarElenco } from './libertadores';
import { clubesDaLiga, jogadoresExternos, paisDaLiga, PAISES_MUNDO } from './ligas-mundo';
import { rodadasDaLiga } from './ligas-mundo-calendario';
import { jogadorDeExterno } from './mercado';
import type { Rng } from './rng';
import type { Clube } from './tipos';

/**
 * Ligas estrangeiras jogáveis: os clubes da planilha viram clubes de verdade (elenco real, força pelo valor de mercado) numa liga de
 * pontos corridos, com o mesmo motor da Série A. A força do clube (nível 8..19) sai da média dos 11 melhores, como nas ligas simuladas.
 */

const PALAVRAS_VAZIAS = new Set(['fc', 'cf', 'sc', 'ac', 'as', 'sv', 'vfl', 'vfb', 'fsv', 'tsg', 'ss', 'us', 'ssc', 'afc', 'rc', 'rcd', 'ud', 'sd', 'cd', 'ca', 'fk', 'de', 'la', 'el', 'del', '1.', '1', 'aa', 'cs', 'sk']);

/** Siglas de 3 letras, únicas dentro da liga ("Manchester City" -> MCI/MAN...). */
function sigla(nome: string, usadas: Set<string>): string {
  const palavras = nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((p) => p && !PALAVRAS_VAZIAS.has(p.toLowerCase()));
  const w = palavras.length ? palavras : [nome];
  const a = (w[0] ?? 'XXX').toUpperCase();
  const b = (w[1] ?? '').toUpperCase();
  const candidatas = [a.slice(0, 3), b ? a.slice(0, 2) + b.slice(0, 1) : '', b ? a.slice(0, 1) + b.slice(0, 2) : '', a.slice(0, 2) + (w[2] ?? b).slice(0, 1).toUpperCase()].filter((x) => x.length === 3);
  const livre = candidatas.find((c) => !usadas.has(c));
  if (livre) {
    usadas.add(livre);
    return livre;
  }
  for (let i = 1; i < 100; i++) {
    const c = `${a.slice(0, 2)}${i % 10}`;
    if (!usadas.has(c)) {
      usadas.add(c);
      return c;
    }
  }
  return a.slice(0, 3);
}

function hash(texto: string): number {
  let h = 2166136261;
  for (let i = 0; i < texto.length; i++) h = Math.imul(h ^ texto.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Duas cores fixas por clube (a planilha não traz cores): derivadas do nome. */
function cores(nome: string): [string, string] {
  const h = hash(nome);
  const matiz = h % 360;
  const claro = (h >> 9) % 2 === 0;
  return [hsl(matiz, 70, claro ? 55 : 38), claro ? '#ffffff' : hsl((matiz + 40) % 360, 60, 85)];
}

/** HSL (graus, %, %) em "#rrggbb" (as telas só aceitam cores hexadecimais). */
function hsl(h: number, s: number, l: number): string {
  const S = s / 100;
  const L = l / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = S * Math.min(L, 1 - L);
  const f = (n: number): string =>
    Math.round(255 * (L - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))))
      .toString(16)
      .padStart(2, '0');
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function ligaMundoPorId(id: string): { id: string; nome: string; pais: string } | undefined {
  const pais = paisDaLiga(id);
  const liga = pais?.ligas.find((l) => l.id === id);
  return pais && liga ? { id, nome: liga.nome, pais: pais.nome } : undefined;
}

/** Todas as ligas estrangeiras que dá para disputar. */
export function ligasJogaveis(): { id: string; nome: string; pais: string }[] {
  return PAISES_MUNDO.flatMap((p) => p.ligas.map((l) => ({ id: l.id, nome: l.nome, pais: p.nome })));
}

/** Calendário da liga: turno e returno; com muitos clubes (mais de 20), só turno, como nas ligas simuladas. */
export function calendarioLigaMundo(ids: number[], rng: Rng): Calendario {
  const c = gerarCalendario(ids, rng);
  const rodadas = rodadasDaLiga(ids.length);
  return c.slice(0, rodadas);
}

/** Ids dos clubes estrangeiros num jogo com o mundo inteiro: ficam acima dos brasileiros (0..39) e dos clubes das copas continentais (1000+). */
export const ID_BASE_MUNDO = 2000;

/**
 * Ligas que têm divisão de acesso: quem sobe e desce a cada temporada (`vagas` clubes de cada lado). A liga 'primeira' é a de cima.
 * Ligas sem segunda divisão no cadastro (Arábia Saudita, Argentina e MLS) não têm rebaixamento.
 */
export const DIVISOES_MUNDO: { primeira: string; segunda: string; vagas: number }[] = [
  { primeira: 'de-1', segunda: 'de-2', vagas: 2 },
  { primeira: 'es-1', segunda: 'es-2', vagas: 3 },
  { primeira: 'fr-1', segunda: 'fr-2', vagas: 2 },
  { primeira: 'en-1', segunda: 'en-2', vagas: 3 },
  { primeira: 'it-a', segunda: 'it-b', vagas: 3 },
];

/** Todos os clubes estrangeiros de uma vez (as 13 ligas), com ids a partir de `ID_BASE_MUNDO` (cada liga ocupa uma faixa de 100). */
export function gerarMundo(rng: Rng, proximoId: { valor: number }): Clube[] {
  return ligasJogaveis().flatMap((l, k) => gerarLigaMundo(l.id, rng, proximoId, ID_BASE_MUNDO + k * 100));
}

/** Os ids das ligas que existem no jogo com o mundo inteiro, pelos clubes que têm `liga`. */
export function ligasDosClubes(clubes: Clube[]): string[] {
  return [...new Set(clubes.map((c) => c.liga).filter((l): l is string => Boolean(l)))];
}

/** Cria os clubes de uma liga estrangeira, com ids `idBase`..`idBase`+n-1 (ordem alfabética), elencos da planilha e valores de mercado reais. */
export function gerarLigaMundo(ligaId: string, rng: Rng, proximoId: { valor: number }, idBase = 0): Clube[] {
  if (!ligaMundoPorId(ligaId)) throw new Error(`liga desconhecida: ${ligaId}`);
  const infos = clubesDaLiga(ligaId);
  const externos = jogadoresExternos().filter((x) => x.ligaId === ligaId);
  const usadas = new Set<string>();
  return infos.map((info, i) => {
    const nivel = Math.max(8, Math.min(19, Math.round((info.forca ?? 35) / 3.5)));
    const c = gerarClube(idBase + i, info.nome, sigla(info.nome, usadas), cores(info.nome), nivel, proximoId, rng);
    c.liga = ligaId;
    if (info.estadio) c.estadio = info.estadio;
    c.tecnico = 'Técnico';
    // capacidade pela força do clube (a sigla pode coincidir com a de um clube brasileiro, então a tabela oficial não vale)
    c.capacidade = Math.round((8000 + Math.max(0, (info.forca ?? 35) - 30) * 1100 + rng.inteiro(4000)) / 100) * 100;
    const jogadores = externos
      .filter((x) => x.clube === info.nome)
      .map((x) => jogadorDeExterno(x, proximoId.valor++, nivel, rng))
      .sort((p, q) => q.overall - p.overall);
    const goleiros = jogadores.filter((j) => j.posicao === 'G').slice(0, 3);
    const outros = jogadores.filter((j) => j.posicao !== 'G').slice(0, 27);
    c.elenco = [...goleiros, ...outros];
    completarElenco(c, proximoId, rng);
    for (const j of c.elenco) j.salario = calcularSalario(j.overall, j.idade, j.posicao, nivel);
    return c;
  });
}
